/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text } from '@components/atoms'

import { useAppSelector } from '@redux/index'
import { OrderType } from '@schemas'
import { blue, grey, pink } from '@mui/material/colors'
import { AsYouType, CountryCode, isValidPhoneNumber } from 'libphonenumber-js'

export const OrderInfoSection = () => {
	// redux state
	const thisOrder: OrderType = useAppSelector(state => state.order.selectedOrder)

	const orderNumber = thisOrder.id
	const address = React.useMemo(() => {
		let res = ''

		if (!['', 'undefined'].includes(thisOrder.address1)) {
			res += thisOrder.address1
		}

		if (!['', 'undefined'].includes(thisOrder.address2)) {
			res += ', '
			res += thisOrder.address2
		}

		if (!['', 'undefined'].includes(thisOrder.city)) {
			res += ', '
			res += thisOrder.city
		}

		if (!['', 'undefined'].includes(thisOrder.state)) {
			res += ', '
			res += thisOrder.state
		}

		if (!['', 'undefined'].includes(thisOrder.country)) {
			res += ', '
			res += thisOrder.country
		}

		return res
	}, [thisOrder])

	const phoneNumber = React.useMemo(() => {
		const isPhoneValid = isValidPhoneNumber(
			thisOrder.phoneNumber,
			thisOrder.countryCode as CountryCode,
		)

		if (isPhoneValid) {
			const asYouType = new AsYouType()

			const phoneNumber = asYouType.input(
				'+' + thisOrder.phoneCountryNumber + thisOrder.phoneNumber,
			)
			const numberInfo = asYouType.getNumber()
			const res =
				numberInfo?.country === undefined
					? phoneNumber
					: `${phoneNumber} (${numberInfo?.country})`

			return res
		} else {
			return '+' + thisOrder.phoneCountryNumber + thisOrder.phoneNumber
		}
	}, [thisOrder])

	console.log(thisOrder)

	return (
		<View css={style}>
			<View className="orderDetailListView">
				<View className="orderDetailOuterView">
					<View className="orderAdditionalInfoView">
						<View className="orderNumberView">
							<Text className="orderNumberLabel" category="c2" hint="500">
								발주번호
							</Text>
							<Text className="orderNumber" category="h6">
								{orderNumber}
							</Text>
						</View>

						<View className="orderInfoOuterView">
							<Text className="orderInfoTitle">고객 정보</Text>

							<View className="detailDataView">
								<View className="customerInfoView">
									<Text className="customerInfoLabel" category="c2" hint="500">
										이름
									</Text>
									<Text className="customerValue" category="s2" hint="700">
										{thisOrder.receiverGivenName}
									</Text>
								</View>

								<View className="customerInfoView">
									<Text className="customerInfoLabel" category="c2" hint="500">
										성
									</Text>
									<Text className="customerValue" category="s2" hint="700">
										{thisOrder.receiverFamilyName}
									</Text>
								</View>
							</View>

							<View className="detailDataView">
								<View className="customerInfoView">
									<Text className="customerInfoLabel" category="c2" hint="500">
										이메일
									</Text>
									<Text className="customerValue" category="s2" hint="700">
										{thisOrder.customerEmail}
									</Text>
								</View>

								<View className="customerInfoView">
									<Text className="customerInfoLabel" category="c2" hint="500">
										연락처
									</Text>
									<Text className="customerValue" category="s2" hint="700">
										{phoneNumber}
									</Text>
								</View>
							</View>
						</View>

						<View className="orderInfoOuterView">
							<Text className="orderInfoTitle">배송지 정보</Text>

							<View className="detailDataView">
								<View className="deliveryInfoView">
									<Text className="customerInfoLabel" category="c2" hint="500">
										주소
									</Text>
									<Text className="customerValue" category="s2" hint="700">
										{address}
									</Text>
								</View>
							</View>
						</View>

						<View className="orderInfoOuterView">
							<Text className="orderInfoTitle">배송 정보</Text>

							<View className="detailDataView">
								<View className="customerInfoView">
									<Text className="customerInfoLabel" category="c2" hint="500">
										배송사
									</Text>
									<Text className="customerValue" category="s2" hint="700">
										{thisOrder.deliveryCompany}
									</Text>
								</View>

								<View className="customerInfoView">
									<Text className="customerInfoLabel" category="c2" hint="500">
										운송장번호
									</Text>
									<Text className="customerValue" category="s2" hint="700">
										{thisOrder.trackingNumber}
									</Text>
								</View>
							</View>
						</View>

						<View className="orderInfoOuterView">
							<Text className="orderInfoTitle">결제 정보</Text>

							<View className="detailDataView cost">
								<View className="costInfoView">
									<Text className="costInfoLabel" category="c2" hint="500">
										제품가
									</Text>
									<Text className="costValue" category="s2" hint="700">
										{thisOrder.totalUsd.toLocaleString('en-US', {
											style: 'currency',
											currency: 'USD',
										})}
									</Text>
								</View>
								<View className="costInfoView">
									<Text className="costInfoLabel" category="c2" hint="500">
										부가세
									</Text>
									<Text className="costValue" category="s2" hint="700">
										{thisOrder.vatUsd.toLocaleString('en-US', {
											style: 'currency',
											currency: 'USD',
										})}
									</Text>
								</View>

								<View className="costInfoView">
									<Text className="costInfoLabel" category="c2" hint="500">
										배송비
									</Text>
									<Text className="costValue" category="s2" hint="700">
										{thisOrder.shippingUsd.toLocaleString('en-US', {
											style: 'currency',
											currency: 'USD',
										})}
									</Text>
								</View>
								<View className="costInfoView">
									<Text className="costInfoLabel" category="c2" hint="500">
										할인
									</Text>
									<Text className="costValue" category="s2" css={{ color: pink['A400'] }}>
										{`- ${thisOrder.discountedUsd.toLocaleString('en-US', {
											style: 'currency',
											currency: 'USD',
										})}`}
									</Text>
								</View>

								<View
									className="costInfoView"
									css={{ borderTop: '1px solid', borderColor: grey[700] }}
								>
									<Text className="costInfoLabel" category="c1" hint="500">
										결제금액
									</Text>
									<Text className="costValue" category="h6" css={{ color: blue['A400'] }}>
										{thisOrder.paidUsd.toLocaleString('en-US', {
											style: 'currency',
											currency: 'USD',
										})}
									</Text>
								</View>
							</View>
						</View>
					</View>
				</View>
			</View>
		</View>
	)
}

export default OrderInfoSection

const style = css({
	'.orderDetailListView': {
		height: '100%',
		overflow: 'auto',

		'.orderDetailOuterView': {
			'.orderAdditionalInfoView': {
				marginBottom: '0.5rem',
				padding: '0.5rem',
				background: 'white',
				border: '1px solid',
				borderColor: grey[300],
				borderRadius: '5px',

				'.orderNumberView': {
					width: '100%',
					marginBottom: '2rem',
					paddingLeft: '0.4rem',
					textAlign: 'left',
					borderBottom: '1px solid',
					borderColor: grey[500],

					'.orderNumberLabel': {
						marginBottom: '0.5rem',
					},
					'.orderNumber': {
						marginTop: '0.2rem',
					},
				},

				'.orderInfoOuterView': {
					marginBottom: '3rem',

					'.orderInfoTitle': {
						marginBottom: 0,
						paddingLeft: '0.5rem',
						paddingBottom: '1rem',
						textAlign: 'left',
						borderBottom: '1px solid',
						borderColor: grey[200],
					},

					'.detailDataView': {
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						padding: '0 0.5rem 0.5rem',

						'.customerInfoView': {
							width: '50%',
							textAlign: 'left',

							'.customerValue': {
								margin: 0,
							},
						},

						'.deliveryInfoView': {
							width: '100%',
							textAlign: 'left',

							'.customerValue': {
								margin: 0,
							},
						},

						'&.cost': {
							display: 'block',
							width: '20rem',
							marginLeft: 'calc(100% - 20rem)',

							'.costInfoView': {
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								width: '100%',

								'.costInfoLabel': {
									margin: '0.5rem 0',
								},
								'.costValue': {
									margin: '0.5rem 0',
								},
							},
						},
					},
				},
			},
		},
	},
})
